import React from "react";
import { format, addDays, startOfWeek, isSameDay, compareAsc } from "date-fns";
import { HiOutlineDotsHorizontal } from "react-icons/hi";
import DayView from "./DayView";
import type { CalendarProps } from "../../types/calendar-props";
import PostCard from "./PostCard";
import { useIsMobile } from "../../hooks/useIsMobile";

interface WeeklyViewProps extends CalendarProps {
    currentDate: Date;
}

const WeeklyView: React.FC<WeeklyViewProps> = ({
    posts,
    currentDate,
    loading,
    handleMoreClick,
    closeDayView,
    selectedDay,
}) => {
    const isMobile = useIsMobile(900);

    const weekStart = startOfWeek(currentDate, { weekStartsOn: 1 });
    const weekDays = Array.from({ length: 7 }, (_, i) => addDays(weekStart, i));

    const renderDayColumn = (day: Date) => {
        const dayPosts = posts
            ?.filter((post) => isSameDay(new Date(post.scheduledFor), day))
            ?.sort((a, b) =>
                compareAsc(new Date(a.scheduledFor), new Date(b.scheduledFor))
            );

        const isToday = isSameDay(day, new Date());
        const postsToShow = dayPosts?.slice(0, 6);
        const hasMorePosts = dayPosts?.length > 6;

        return (
            <div
                key={day.toString()}
                className="flex flex-col h-full"
                style={{
                    backgroundColor: isToday
                        ? "rgba(240, 223, 175, 0.5)"
                        : "transparent",
                    borderRadius: isToday ? "8px" : "0",
                }}
            >
                <div
                    className={`text-sm font-semibold py-1 px-2.5 border-b ${
                        isToday ? "bg-lightSecondary" : "bg-white"
                    }`}
                    style={{
                        position: "sticky",
                        top: 0,
                        zIndex: 10,
                    }}
                >
                    <div
                        className={`flex items-center ${
                            isMobile ? "justify-start" : "justify-center"
                        }`}
                    >
                        {isMobile ? format(day, "EEE d") : format(day, "EEEE")}
                    </div>
                </div>
                <div className="flex-1 space-y-4 px-2 py-2 border border-gray-200">
                    {!isMobile && (
                        <div className="text-xs font-semibold">
                            {format(day, "d")}
                        </div>
                    )}
                    {postsToShow?.map((post, index) => (
                        <PostCard
                            date={format(day, "EEE-d")}
                            idx={index}
                            key={post.id}
                            post={post}
                        />
                    ))}
                    {hasMorePosts && (
                        <div
                            className="flex justify-center items-center cursor-pointer mt-2"
                            onClick={() => handleMoreClick(day)}
                        >
                            <HiOutlineDotsHorizontal
                                size={20}
                                className="text-primary"
                            />
                        </div>
                    )}
                </div>
            </div>
        );
    };

    return (
        <>
            <div
                className={`grid grid-cols-${
                    isMobile ? "1" : "7"
                } gap-0 h-full overflow-x-auto`}
            >
                {weekDays.map(renderDayColumn)}
            </div>
            {selectedDay && (
                <DayView
                    day={selectedDay}
                    posts={posts}
                    onClose={closeDayView}
                    loading={loading}
                />
            )}
        </>
    );
};

export default WeeklyView;
